import './styles/ArticlesList.scss';

import { useApolloClient, useQuery } from '@apollo/client';
import { Chip, Grid, Icon, Pagination, PaginationItem, Paper, Tooltip } from '@mui/material';

import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import ALLOWED_TO_POST from 'gql/queries/PermissionProvider/testAllowedToPost.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';
import QUERY from 'gql/queries/getArticles.gql';

import { Filter } from 'app/components';
import { CreateButton } from 'app/components/CreateButton';
import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { LinkButton } from 'app/components/LinkButton';
import { Modal } from 'app/components/Modal';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { ArticleItem } from 'app/components/RichList/items/ArticleItem';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';

import Article from '../Article';

interface Props {
  condensed?: boolean;
  heading?: string;
  scope?: 'today' | 'past' | 'future' | 'full' | 'withoutPast';
}

export function ArticlesList(props: Props) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const userData = client.readQuery({ query: USER_QUERY });
  const aclHelper = new ACLHelper(userData.currentUser);
  const allowedToPostQuery = useQuery<any, any>(ALLOWED_TO_POST);
  const [listScope, setListScope] = useState(props.scope || 'withoutPast');
  const [listMyArticles, setListMyArticles] = useState('all');
  const location = useLocation();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();
  usePageHeading(props.condensed ? '' : t(translations.pages.articles.title()));

  const search = location.search;
  const params = new URLSearchParams(search);
  const defaultFilters = {};
  params.forEach((v, k) => {
    defaultFilters[k] = {
      exact: true,
      term: v
    };
  });
  const page = params.get('page');

  useEffect(() => {
    if (!page && !props.condensed) {
      navigate({ pathname: '/articles', search: '?page=1' });
    }
  }, [navigate, page, props.condensed]);

  let initialOffset = 0;
  let initialPage = 1;
  if (page && !props.condensed) {
    initialPage = parseInt(page, 10);
    initialOffset = (parseInt(page, 10) - 1) * 10;
  }

  const mapParamsToFilters = (
    pms: string[]
  ): { [key: string]: { exact: boolean; term: string } } => {
    const filters = {};
    pms.forEach(p => {
      const pv = params.get(p);
      if (pv) {
        filters[p] = { exact: false, term: pv };
      }
    });
    return filters;
  };

  const filters = mapParamsToFilters(['keyword', 'startDate', 'endDate', 'articletype']);
  const isAdminUser = aclHelper.isSuperUser();

  const articlesListQuery = useQuery<any, any>(QUERY, {
    variables: {
      scope: listScope,
      userScope: listMyArticles,
      offset: initialOffset,
      filter: JSON.stringify(filters)
    },
    fetchPolicy: 'cache-and-network'
  });
  const { refetch } = articlesListQuery;

  const [keywordInputState, setKeywordInputState] = useState(filters.keyword?.term || '');
  const [articletypeInputState, setArticletypeInputState] = useState(
    filters.articletype?.term || ''
  );
  const [startDateInputState, setStartDateInputState] = useState<Date | null>(
    filters.startDate ? new Date(filters.startDate.term) : null
  );
  const [endDateInputState, setEndDateInputState] = useState<Date | null>(
    filters.endDate ? new Date(filters.endDate.term) : null
  );
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const splitList = location.search.replace('?', '').split('&');
  const newSearchParameters = splitList.filter(element => !element.startsWith('page='));
  let linkTo = '';
  if (newSearchParameters.length !== 0) {
    linkTo = '&' + newSearchParameters.join('&');
  }

  const handlePagination = (event, page: number) => {
    const offset = (page - 1) * 10;
    refetch({ offset: offset });
  };

  const toggleScope = () => {
    if (listScope === 'withoutPast') {
      setListScope('past');
    } else {
      setListScope('withoutPast');
    }
  };

  const toggleMyArticles = () => {
    if (listMyArticles !== 'self') {
      setListMyArticles('self');
    } else {
      setListMyArticles('all');
    }
  };

  const cancelArticleCreation = (event?, reason?) => {
    if (reason && reason === 'backdropClick') return;
    if (disabledCancel) {
      return;
    }
    setDisabledCancel(true);
    setCreateModalOpen(false);
    resetFilter();
    setDisabledCancel(false);
  };

  const resetFilter = () => {
    setKeywordInputState('');
    setArticletypeInputState('');
    setStartDateInputState(null);
    setEndDateInputState(null);
    refetch({ filter: undefined });
  };

  const richlistFilter: Filter[] | undefined = props.condensed
    ? undefined
    : [
        {
          field: 'keyword',
          label: t(translations.pages.articles.filter.keyword()),
          stateHandler: [keywordInputState, setKeywordInputState],
          large: true
        },
        {
          field: 'startDate',
          label: t(translations.pages.articles.filter.startDate()),
          datePicker: {
            startTimeAtDay: true,
            stateHandler: [startDateInputState, setStartDateInputState]
          }
        },
        {
          field: 'endDate',
          label: t(translations.pages.articles.filter.endDate()),
          datePicker: {
            startTimeAtDay: false,
            stateHandler: [endDateInputState, setEndDateInputState]
          }
        },
        {
          field: 'articletype',
          label: t(translations.pages.articles.filter.articletype()),
          select: {
            options: [
              {
                label: '---',
                name: null
              },
              {
                label: t(translations.pages.articles.type.general()),
                name: 'general'
              },
              {
                label: t(translations.pages.articles.type.study()),
                name: 'study'
              },
              {
                label: t(translations.pages.articles.type.event()),
                name: 'event'
              },
              {
                label: t(translations.pages.articles.type.legal()),
                name: 'legal'
              },
              {
                label: t(translations.pages.articles.type.holiday()),
                name: 'holiday'
              }
            ],
            stateHandler: [articletypeInputState, setArticletypeInputState]
          }
        }
      ];

  return (
    <>
      <ErrorBoundary boundaryTag="articleDetailBoundary">
        <Modal
          open={createModalOpen}
          onClose={cancelArticleCreation}
          headline={t(translations.pages.articles.create.title())}
          onCancel={() => {
            cancelArticleCreation();
          }}
          disabledCancel={disabledCancel}
        >
          <Article
            onCancel={() => cancelArticleCreation()}
            closeHandler={() => {
              cancelArticleCreation();
            }}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <Grid container className="articlesList">
        {!props.condensed && (
          <Grid container className="articlesList__filters">
            <Chip
              label={t(translations.pages.articles.myArticles())}
              clickable
              component={Paper}
              onClick={toggleMyArticles}
              className={clsx('articlesList__filters__filter', {
                'articlesList__filters__filter--active': listMyArticles === 'self'
              })}
            />
            <Tooltip title={t(translations.pages.articles.tooltips.articleArchive())}>
              <Chip
                label={t(translations.pages.articles.pastArticles())}
                clickable
                component={Paper}
                onClick={toggleScope}
                className={clsx('articlesList__filters__filter', {
                  'articlesList__filters__filter--active': listScope === 'past'
                })}
              />
            </Tooltip>
          </Grid>
        )}
        <Grid container component={Paper} elevation={4}>
          <Grid container className="articlesList__heading">
            {props.heading || t(translations.pages.home.articlesList.heading())}
          </Grid>
          <Grid container className="articlesList__body">
            <GQLGuard query={[allowedToPostQuery, articlesListQuery]}>
              {([dataAllowedToPost, data]) => {
                if (initialPage !== 1 && data.articles.length === 0) {
                  navigate({ pathname: '/articles', search: '?page=1' + linkTo });
                }

                const pages = Math.ceil(data.articlesCount / 10);
                return (
                  <ErrorBoundary boundaryTag="articlesListBoundary">
                    <RichList
                      defaultFilters={defaultFilters}
                      filters={richlistFilter}
                      className={props.condensed ? 'articlesList__body__scroll' : ''}
                      controls={
                        (dataAllowedToPost.allowedToPost || isAdminUser) && !props.condensed ? (
                          <>
                            <CreateButton
                              onClick={() => {
                                setCreateModalOpen(true);
                              }}
                            >
                              {t(translations.common.create())}
                            </CreateButton>
                          </>
                        ) : undefined
                      }
                      onFilterChange={refetch}
                    >
                      {data.articles.length === 0 ? (
                        <NoItems
                          icon={
                            props.condensed ? (
                              <Icon className="far fa-smile-beam" />
                            ) : (
                              <Icon className="fas fa-info-circle" />
                            )
                          }
                          message={
                            props.condensed
                              ? t(translations.pages.articles.noEventsToday())
                              : t(translations.pages.articles.noArticles())
                          }
                        />
                      ) : (
                        <>
                          {data.articles.length === 0 ? (
                            <NoItems />
                          ) : (
                            <Grid container>
                              {data.articles.map(a => (
                                <ErrorBoundary key={a.id} boundaryTag="articleItemBoundary">
                                  <ArticleItem
                                    article={a}
                                    key={a.id}
                                    dense={props.condensed}
                                    back={currentURL}
                                  />
                                </ErrorBoundary>
                              ))}
                            </Grid>
                          )}
                        </>
                      )}
                    </RichList>
                    {pages > 1 ? (
                      <Grid container className="articlesList__body__pagination">
                        <Pagination
                          count={pages}
                          onChange={handlePagination}
                          defaultPage={initialPage}
                          renderItem={item => (
                            <PaginationItem
                              component={Link}
                              to={`/articles${`?page=${item.page}`}` + linkTo}
                              {...item}
                            />
                          )}
                        ></Pagination>
                      </Grid>
                    ) : null}
                  </ErrorBoundary>
                );
              }}
            </GQLGuard>
            {props.condensed && (
              <LinkButton href="/articles" linkText={t(translations.pages.articles.viewAll())} />
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
